import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useLifecycleEventsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const DetailDialog = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const DetailCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const DetailRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  &:last-child { border-bottom: none; }
`;

const DetailLabel = styled.div`
  flex: 0 0 180px;
  color: #64748b;
  font-weight: 500;
`;

const DetailValue = styled.div`
  flex: 1;
  word-break: break-all;
`;

const PayloadPre = styled.pre`
  background: #f5f7fa;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  margin: 8px 0 0;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 320px;
  overflow: auto;
`;

// #72 — yerel CloseButton kaldırıldı, paylaşılan Button'a bağlandı (bkz.
// t6-report.md).

export default function LifecycleEventsPage() {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [retryConfirm, setRetryConfirm] = useState({ isOpen: false, id: null, eventCode: '' });
  const [retrying, setRetrying] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const { fetching, data, pagination, columns: baseColumns, retryDlq, getDetail } = useLifecycleEventsApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // Row action: open detail (also shows full payload).
  const handleViewDetail = useCallback(async (row) => {
    setDetailLoading(true);
    try {
      const d = await getDetail(row.id);
      if (d) setDetail(d);
    } finally {
      setDetailLoading(false);
    }
  }, [getDetail]);

  // Row action: only available when row is in DLQ.
  const handleRetryClick = useCallback((row) => {
    setRetryConfirm({ isOpen: true, id: row.id, eventCode: row.eventCode });
  }, []);

  const handleRetryConfirm = useCallback(async () => {
    if (!retryConfirm.id) return;
    setRetrying(true);
    await retryDlq(retryConfirm.id);
    setRetryConfirm({ isOpen: false, id: null, eventCode: '' });
    setRetrying(false);
  }, [retryConfirm.id, retryDlq]);

  // Each row's action menu is computed per-row so the Retry option only
  // shows up for rows currently in DLQ — preventing accidental re-runs of
  // already-processed events.
  const buildActions = useCallback((row) => {
    const actions = [
      { type: 'details', label: t('lifecycleEvent.viewDetail'), onClick: handleViewDetail },
    ];
    if (row?.derivedState === 'dlq') {
      actions.push(
        { type: 'divider' },
        { type: 'edit', label: t('lifecycleEvent.retry'), onClick: handleRetryClick },
      );
    }
    return actions;
  }, [t, handleViewDetail, handleRetryClick]);

  const columns = useMemo(() => {
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={buildActions(row)} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={retryConfirm.isOpen}
        title={t('lifecycleEvent.retryTitle')}
        message={t('lifecycleEvent.retryMessage', { eventCode: retryConfirm.eventCode })}
        onConfirm={handleRetryConfirm}
        onCancel={() => setRetryConfirm({ isOpen: false, id: null, eventCode: '' })}
        loading={retrying}
      />

      <DetailDialog $open={!!detail}>
        {detail && (
          <DetailCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('lifecycleEvent.detailTitle')}</h3>
            <DetailRow><DetailLabel>{t('lifecycleEvent.eventCode')}</DetailLabel><DetailValue>{detail.eventCode}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.eventVersion')}</DetailLabel><DetailValue>{detail.eventVersion}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.state.label')}</DetailLabel><DetailValue>{detail.derivedState}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.occurredAt')}</DetailLabel><DetailValue>{detail.occurredAt}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.idempotencyKey')}</DetailLabel><DetailValue>{detail.idempotencyKey}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.personId')}</DetailLabel><DetailValue>{detail.personId || '-'}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.actorUserId')}</DetailLabel><DetailValue>{detail.actorUserId || '-'}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.processedAt')}</DetailLabel><DetailValue>{detail.processedAt || '-'}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.retryCount')}</DetailLabel><DetailValue>{detail.retryCount}</DetailValue></DetailRow>
            <DetailRow><DetailLabel>{t('lifecycleEvent.dlqAt')}</DetailLabel><DetailValue>{detail.dlqAt || '-'}</DetailValue></DetailRow>
            {detail.lastError && (
              <DetailRow><DetailLabel>{t('lifecycleEvent.lastError')}</DetailLabel><DetailValue style={{ color: '#E53935' }}>{detail.lastError}</DetailValue></DetailRow>
            )}
            <DetailRow>
              <DetailLabel>{t('lifecycleEvent.payload')}</DetailLabel>
              <DetailValue>
                <PayloadPre>{(() => {
                  try { return JSON.stringify(JSON.parse(detail.payloadJson || '{}'), null, 2); }
                  catch { return detail.payloadJson; }
                })()}</PayloadPre>
              </DetailValue>
            </DetailRow>
            <Button style={{ marginTop: 16 }} onClick={() => setDetail(null)}>{t('common.close')}</Button>
          </DetailCard>
        )}
      </DetailDialog>

      {useMemo(() => (
        <DataTable
          title={t('lifecycleEvent.title')}
          fetching={fetching || detailLoading}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, detailLoading, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
